import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast, type UseToastOptions } from "@chakra-ui/react";
import axios from "axios";
import { GET_ACCOUNT_ME_QUERY_KEY } from "../../../components/auth/api/authApi";
import {
  postChangeEmail,
  postChangePassword,
  postClaimCredit,
  postAvatarUpload,
  getEmailSettings,
  postEmailSettings,
} from "../api/settingsApi";

const TOAST_ID = "settings";

type SettingsFormFieldError = {
  name?: string;
  error?: string | null;
};

type SettingsPayloadErrors = Record<string, { message?: string } | undefined>;

type SettingsErrorResponse = {
  error?: string;
  payload?: {
    form_fields?: SettingsFormFieldError[];
  };
  errors?: {
    payload?: SettingsPayloadErrors;
  };
};

function getSettingsErrorResponse(error: unknown): SettingsErrorResponse | null {
  if (axios.isAxiosError<SettingsErrorResponse>(error)) {
    return error.response?.data ?? null;
  }

  if (typeof error !== "object" || error === null || !("response" in error)) {
    return null;
  }

  const response = error.response;
  if (typeof response !== "object" || response === null || !("data" in response)) {
    return null;
  }

  return response.data as SettingsErrorResponse;
}

export function useSettingsToast() {
  const toast = useToast();
  const show = (opts: UseToastOptions) => {
    if (toast.isActive(TOAST_ID)) {
      toast.update(TOAST_ID, opts);
    } else {
      toast({ id: TOAST_ID, ...opts });
    }
  };
  return {
    success: (title: string) =>
      show({ title, status: "success", duration: 3000, isClosable: true }),
    error: (title: string, description?: string) =>
      show({
        title,
        description: description || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      }),
  };
}

export function extractFormFieldError(error: unknown, fieldName?: string): string | null {
  const errorData = getSettingsErrorResponse(error);
  const formFieldErrors = errorData?.payload?.form_fields;
  if (!Array.isArray(formFieldErrors)) {
    return null;
  }

  const matchingField = fieldName
    ? formFieldErrors.find((field) => field.name === fieldName && field.error)
    : formFieldErrors.find((field) => field.error);

  return matchingField?.error ?? null;
}

export function extractErrorMessage(error: unknown): string {
  const formFieldError = extractFormFieldError(error);
  if (formFieldError) {
    return formFieldError;
  }

  const errorData = getSettingsErrorResponse(error);
  if (errorData) {
    const serverErrors = errorData.errors?.payload;
    if (serverErrors) {
      const firstError = Object.values(serverErrors)[0];
      return firstError?.message || "Something went wrong";
    }

    return errorData.error || "Something went wrong";
  }

  return "Something went wrong";
}

export function useChangeEmailMutation() {
  const queryClient = useQueryClient();
  const toast = useSettingsToast();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      postChangeEmail(email, password),
    onSuccess: (response) => {
      queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, response);
      toast.success("Email updated");
    },
    onError: (error: unknown) => {
      toast.error("Failed to update email", extractErrorMessage(error));
    },
  });
}

export function useChangePasswordMutation() {
  const toast = useSettingsToast();

  return useMutation({
    mutationFn: ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) =>
      postChangePassword(oldPassword, newPassword),
    onSuccess: () => {
      toast.success("Password changed");
    },
    onError: (error: unknown) => {
      toast.error("Failed to change password", extractErrorMessage(error));
    },
  });
}

export const EMAIL_SETTINGS_QUERY_KEY = ["email-settings"];

export function useEmailSettingsQuery() {
  return useQuery({
    queryKey: EMAIL_SETTINGS_QUERY_KEY,
    queryFn: () => getEmailSettings(),
    select: (response) => response.data?.payload?.results?.email_settings ?? [],
  });
}

export function useEmailSettingsMutation() {
  const queryClient = useQueryClient();
  const toast = useSettingsToast();

  return useMutation({
    mutationFn: (settings: Record<string, boolean>) => postEmailSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAIL_SETTINGS_QUERY_KEY });
      toast.success("Notification preferences saved");
    },
    onError: (error: unknown) => {
      toast.error("Failed to save notification preferences", extractErrorMessage(error));
    },
  });
}

export function useClaimCreditMutation() {
  const queryClient = useQueryClient();
  const toast = useSettingsToast();

  return useMutation({
    mutationFn: (token: string) => postClaimCredit(token),
    onSuccess: (response) => {
      queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, response);
      toast.success("Code claimed successfully");
    },
    onError: (error: unknown) => {
      toast.error("Failed to claim code", extractErrorMessage(error));
    },
  });
}

export function useAvatarUploadMutation() {
  const queryClient = useQueryClient();
  const toast = useSettingsToast();

  return useMutation({
    mutationFn: (file: File) => postAvatarUpload(file),
    onSuccess: (response) => {
      queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, response);
      toast.success("Avatar updated");
    },
    onError: (error: unknown) => {
      toast.error("Failed to upload avatar", extractErrorMessage(error));
    },
  });
}
